import React, { useEffect, useMemo, useRef } from 'react';
import { remeasureMonacoFontsWhenReady } from '../../utils/monacoFonts';
import Editor from '@monaco-editor/react';
// No static `monaco-editor` import — the barrel would re-pull the ~6.5 MB TS
// language service trimmed from the bundle. Everything reads the live
// instance passed to beforeMount/onMount (same rule as CodeEditor.js).
import { useThemeStore } from '../../store/themeStore';
import { registerMonacoThemes, resolveMonacoTheme } from '../../utils/monacoThemes';
import { statementRangeAtOffset } from '../dbSqlStatements';
import { parseTableRefs, suggestTableAlias, namesInUse } from './sqlTableAlias';
import './SqlQueryEditor.css';

/** Briefly mark what Ctrl+Enter is about to run, so the user sees which statement it picked. */
function flashRange(editor, monaco, model, range) {
  const start = model.getPositionAt(range.start);
  const end = model.getPositionAt(range.end);
  const decorations = editor.createDecorationsCollection?.([
    {
      range: new monaco.Range(start.lineNumber, start.column, end.lineNumber, end.column),
      options: { className: 'dbm-sql-run-flash', isWholeLine: false },
    },
  ]);
  if (decorations) setTimeout(() => decorations.clear(), 700);
}

/**
 * Compact SQL editor for DB Manager query tabs: monarch highlighting (the
 * 'sql' basic language, no worker) plus schema-aware completions.
 *
 * Monaco completion providers are global per language, so ONE provider is
 * registered lazily and routes per-editor: each mounted editor registers its
 * schema callbacks in `schemaByModel` keyed by its model URI, and the
 * provider looks the callbacks up from the model it's invoked on. `schema` is
 * `{ listTables: () => Promise<string[]>, listColumns: (table) => Promise<string[]> }`.
 */

const schemaByModel = new Map(); // model uri string -> { listTables, listColumns }
let sqlProviderRegistered = false;

const SQL_KEYWORDS = [
  'SELECT', 'FROM', 'WHERE', 'AND', 'OR', 'NOT', 'NULL', 'AS', 'JOIN', 'LEFT JOIN', 'RIGHT JOIN',
  'INNER JOIN', 'ON', 'GROUP BY', 'ORDER BY', 'HAVING', 'LIMIT', 'OFFSET', 'DISTINCT',
  'INSERT INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE FROM', 'CREATE TABLE', 'ALTER TABLE',
  'DROP TABLE', 'LIKE', 'IN', 'IS', 'BETWEEN', 'UNION', 'COUNT', 'SUM', 'AVG', 'MIN', 'MAX',
  'ASC', 'DESC', 'CASE', 'WHEN', 'THEN', 'ELSE', 'END', 'EXISTS',
];

// Table references and aliases: ./sqlTableAlias.js.

/**
 * Monaco language id for a DB engine.
 *
 * Not cosmetic: the generic 'sql' grammar knows only `--` and block comments,
 * so MySQL's `#` line comment was not treated as a comment at all — `# this
 * QUERY` left QUERY highlighted as a keyword. Postgres deliberately keeps the
 * generic-but-correct 'pgsql', where `#` is NOT a comment, which is why this
 * maps per engine instead of teaching one grammar every dialect's syntax.
 */
export function sqlLanguageFor(engine) {
  switch (engine) {
    case 'mysql':
    case 'mariadb':
      return 'mysql';
    case 'postgres':
    case 'postgresql':
      return 'pgsql';
    // SQLite and anything unrecognised: ANSI-ish, `--` comments.
    default:
      return 'sql';
  }
}

// Every id the editor can actually use. Completions are registered per
// language in Monaco, so switching the id without this makes schema-aware
// completion silently stop working on exactly the engines people use most.
const SQL_LANGUAGE_IDS = ['sql', 'mysql', 'pgsql'];

/**
 * The completions for one request — the provider below routes to it with the
 * editor's schema callbacks. Exported for tests (a fake model is enough).
 */
export async function sqlCompletions(model, position, context, schema, Kind) {
  if (!schema) return { suggestions: [] };

    const word = model.getWordUntilPosition(position);
    const range = {
      startLineNumber: position.lineNumber,
      endLineNumber: position.lineNumber,
      startColumn: word.startColumn,
      endColumn: word.endColumn,
    };
    const textBefore = model.getValueInRange({
      startLineNumber: 1,
      startColumn: 1,
      endLineNumber: position.lineNumber,
      endColumn: position.column,
    });
    // Only the statement under the cursor: an alias from another statement
    // in the script must not resolve here (it used to — first match won).
    const offset = model.getOffsetAt(position);
    const stmt = statementRangeAtOffset(model.getValue(), offset, { engine: schema.engine?.() });
    const refs = parseTableRefs(stmt ? stmt.sql : textBefore);

    // `alias.` / `table.` → that table's columns (the user.name case).
    const beforeWord = model.getLineContent(position.lineNumber).slice(0, word.startColumn - 1);
    const dotMatch = beforeWord.match(/([A-Za-z_][\w$]*)\.$/);
    if (dotMatch) {
      const qualifier = dotMatch[1].toLowerCase();
      const ref = refs.find(
        (r) => (r.alias || '').toLowerCase() === qualifier || r.table.toLowerCase() === qualifier
      );
      let tableName = ref?.table;
      if (!tableName) {
        const tables = await schema.listTables().catch(() => []);
        tableName = tables.find((t) => t.toLowerCase() === qualifier);
      }
      if (!tableName) return { suggestions: [] };
      const cols = await schema.listColumns(tableName).catch(() => []);
      return {
        suggestions: cols.map((c) => ({
          label: c,
          kind: Kind.Field,
          detail: tableName,
          insertText: c,
          range,
        })),
      };
    }

    const tables = await schema.listTables().catch(() => []);

    // Right after FROM/JOIN/UPDATE/INSERT INTO → table names only. This is
    // also why ' ' is a trigger character: typing "FROM " pops the table
    // list immediately, matching the WHERE filter input's behavior.
    const tail = textBefore.replace(/\s+/g, ' ').toLowerCase();
    const tableSlot = tail.match(/\b(from|join|into|update|table)\s+[\w`"$]*$/);
    if (tableSlot) {
      // After FROM / JOIN the pick comes with an alias — `fe_users fu` —
      // like DataGrip and DBeaver (owner, 30.09.2026). Not after INTO /
      // UPDATE / TABLE, and not when the user already typed one.
      const withAlias = /^(from|join)$/.test(tableSlot[1]);
      const after = model.getLineContent(position.lineNumber).slice(word.endColumn - 1);
      const aliasTyped = /^\s+(?:as\s+)?[A-Za-z_]/i.test(after);
      const used = namesInUse(refs);
      return {
        suggestions: tables.map((t) => {
          const alias = withAlias && !aliasTyped ? suggestTableAlias(t, used) : null;
          return {
            label: t,
            kind: Kind.Struct,
            detail: alias ? `table · alias ${alias}` : 'table',
            insertText: alias ? `${t} ${alias}` : t,
            filterText: t,
            range,
          };
        }),
      };
    }

    // A plain space anywhere else shouldn't pop a dropdown mid-sentence —
    // only explicit typing (quick suggestions) or '.' does.
    if (context?.triggerCharacter === ' ') {
      return { suggestions: [] };
    }

    // Anywhere else: columns of the statement's tables first, then tables,
    // then keywords — ranked via sortText prefixes.
    const columnSets = await Promise.all(
      refs.map((r) => schema.listColumns(r.table).catch(() => []))
    );
    const cols = [...new Set(columnSets.flat())];
    return {
      suggestions: [
        ...cols.map((c) => ({
          label: c,
          kind: Kind.Field,
          insertText: c,
          range,
          sortText: `1${c}`,
        })),
        ...tables.map((t) => ({
          label: t,
          kind: Kind.Struct,
          detail: 'table',
          insertText: t,
          range,
          sortText: `2${t}`,
        })),
        ...SQL_KEYWORDS.map((k) => ({
          label: k,
          kind: Kind.Keyword,
          insertText: k,
          range,
          sortText: `3${k}`,
        })),
      ],
    };
}

function ensureSqlCompletionProvider(monaco) {
  if (sqlProviderRegistered) return;
  sqlProviderRegistered = true;

  // If the webpack Monaco build doesn't include the 'sql' basic language
  // (e.g. dev server not restarted after the craco languages change), Monaco
  // silently maps unknown language ids to 'plaintext' — and a provider
  // registered for 'sql' never matches the model. Registering the bare id
  // keeps completions working either way (highlighting still needs the
  // bundled monarch grammar).
  for (const id of SQL_LANGUAGE_IDS) {
    if (!monaco.languages.getLanguages().some((l) => l.id === id)) {
      monaco.languages.register({ id });
    }
  }

  const registerFor = (languageId) =>
    monaco.languages.registerCompletionItemProvider(languageId, {
    triggerCharacters: ['.', ' '],
    provideCompletionItems: (model, position, context) =>
      sqlCompletions(model, position, context, schemaByModel.get(model.uri.toString()), monaco.languages.CompletionItemKind),
    });

  // One provider per language id, all sharing the same implementation — the
  // schema lookup is keyed by model URI, so which dialect the model uses makes
  // no difference to what is suggested.
  SQL_LANGUAGE_IDS.forEach(registerFor);
}

export default function SqlQueryEditor({
  value,
  onChange,
  onRun,
  onRunScript,
  schema,
  height = 190,
  placeholder,
  apiRef,
  engine,
}) {
  const theme = useThemeStore((s) => s.theme);
  const isDark =
    theme === 'dark' ||
    (theme === 'system' && typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const onRunRef = useRef(onRun);
  onRunRef.current = onRun;
  const onRunScriptRef = useRef(onRunScript);
  onRunScriptRef.current = onRunScript;
  // Query→query tab switches reuse this editor, so read the engine late.
  const engineRef = useRef(engine);
  engineRef.current = engine;
  const schemaRef = useRef(schema);
  schemaRef.current = schema;
  const modelUriRef = useRef(null);

  // Stable per-mount schema shim so the provider always sees fresh callbacks
  // without re-registering anything when the tab's schema identity changes.
  const schemaShim = useMemo(
    () => ({
      listTables: (...a) => schemaRef.current?.listTables?.(...a) ?? Promise.resolve([]),
      listColumns: (...a) => schemaRef.current?.listColumns?.(...a) ?? Promise.resolve([]),
      engine: () => engineRef.current,
    }),
    []
  );

  useEffect(
    () => () => {
      if (modelUriRef.current) schemaByModel.delete(modelUriRef.current);
    },
    []
  );

  return (
    <div className="dbm-sql-editor" style={{ height }}>
      <Editor
        language={sqlLanguageFor(engine)}
        value={value}
        theme={resolveMonacoTheme(isDark)}
        beforeMount={(monaco) => {
          registerMonacoThemes(monaco);
          ensureSqlCompletionProvider(monaco);
        }}
        onMount={(editor, monaco) => {
          remeasureMonacoFontsWhenReady(monaco);
          const uri = editor.getModel()?.uri.toString();
          if (uri) {
            modelUriRef.current = uri;
            schemaByModel.set(uri, schemaShim);
          }
          editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => {
            onRunRef.current?.();
          });
          // The whole script only on purpose — Ctrl+Enter never falls back to it.
          editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.Enter, () => {
            onRunScriptRef.current?.();
          });
          editor.updateOptions({ placeholder });
          // A query tab exists to be typed in — grab focus on mount (fresh
          // open or table→query tab switch; query→query switches don't
          // remount and use apiRef.focus from the host instead).
          editor.focus();
          // Imperative surface for the host component: the Run action
          // executes only the selection when one exists (long scripts,
          // run-statement-at-a-time workflow).
          if (apiRef) {
            apiRef.current = {
              focus: () => editor.focus(),
              // Run priority: the selection exactly as marked (several lines,
              // empty lines, several statements — never split) → the statement
              // under the cursor (`;` or an empty line ends one) → nothing.
              // Owner, 30.09.2026: NO fallback to the whole script; that ran an
              // UPDATE further down a script someone only meant to SELECT from.
              getRunnableSql: () => {
                const model = editor.getModel();
                if (!model) return '';
                const selection = editor.getSelection();
                const selected = selection && !selection.isEmpty() ? model.getValueInRange(selection) : '';
                if (selected.trim()) return selected;
                const position = editor.getPosition();
                if (!position) return '';
                const range = statementRangeAtOffset(model.getValue(), model.getOffsetAt(position), { engine: engineRef.current });
                if (!range) return '';
                flashRange(editor, monaco, model, range);
                return range.sql;
              },
            };
          }
        }}
        onChange={(v) => onChange(v ?? '')}
        options={{
          minimap: { enabled: false },
          lineNumbers: 'on',
          lineNumbersMinChars: 3,
          fontSize: 12.5,
          scrollBeyondLastLine: false,
          wordWrap: 'on',
          folding: false,
          renderLineHighlight: 'none',
          overviewRulerLanes: 0,
          hideCursorInOverviewRuler: true,
          scrollbar: { verticalScrollbarSize: 8, horizontalScrollbarSize: 8 },
          padding: { top: 8, bottom: 8 },
          // The suggest widget must escape this small clipped container.
          fixedOverflowWidgets: true,
          quickSuggestions: true,
          suggestOnTriggerCharacters: true,
          automaticLayout: true,
          tabSize: 2,
        }}
      />
    </div>
  );
}
